import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { IVFItem } from '../../data/ivfItems';
import BlogModal from './BlogModal';
import '../../assets/styles/IVFSlider.scss';

type Props = {
  title?: string;
  items: IVFItem[];
  viewAllHref?: string;
  accent?: string;
};

const IVFSlider: React.FC<Props> = ({
  title = 'Tüp Bebek',
  items,
  viewAllHref,
  accent = '#d06bcc',
}) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IVFItem | null>(null);

  // Kaç sayfa var (her sayfada 3 kart)
  const totalPages = Math.ceil(items.length / 3);

  // Scroll pozisyonuna göre aktif sayfa index'i takip et
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const onScroll = () => {
      const scrollLeft = el.scrollLeft;
      const scrollWidth = el.scrollWidth - el.clientWidth;
      const pageIndex = Math.round((scrollLeft / scrollWidth) * (totalPages - 1));
      setActiveIndex(Math.max(0, Math.min(totalPages - 1, pageIndex)));
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [totalPages]);

  // Otomatik kayma (5 saniyede bir sayfa)
  useEffect(() => {
    if (isUserInteracting || totalPages <= 1) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => {
        const nextIndex = prev + 1 >= totalPages ? 0 : prev + 1;
        goToPage(nextIndex);
        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [totalPages, isUserInteracting]);

  // Kullanıcı etkileşimini algıla
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const handleInteraction = () => {
      setIsUserInteracting(true);
      setTimeout(() => setIsUserInteracting(false), 10000); // 10 saniye sonra otomatik kayma devam eder
    };

    el.addEventListener('touchstart', handleInteraction);
    el.addEventListener('mousedown', handleInteraction);

    return () => {
      el.removeEventListener('touchstart', handleInteraction);
      el.removeEventListener('mousedown', handleInteraction);
    };
  }, []);

  const goToPage = (pageIndex: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const scrollWidth = el.scrollWidth - el.clientWidth;
    const scrollLeft = (scrollWidth / (totalPages - 1)) * pageIndex;
    el.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  };

  const styleVars = useMemo(
    () =>
      ({
        ['--ivf-accent' as any]: accent,
      } as React.CSSProperties),
    [accent]
  );

  return (
    <div className="ivf-slider" style={styleVars}>
      <div className="ivf-container">
        <div className="ivf-head">
          <h2 className="ivf-title">{title}</h2>
        </div>

        <div className="ivf-scroller" ref={scrollerRef} aria-label={`${title} içerikleri`}>
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              className="ivf-card"
              onClick={() => it.content ? setSelectedItem(it) : null}
              aria-label={`${it.title} detaylarını gör`}
            >
              <div className="ivf-media">
                <img src={it.image} alt={it.title} loading="lazy" />
                <div className="ivf-gradient" />
              </div>
              <div className="ivf-caption">
                <h3 className="ivf-card-title">{it.title}</h3>
                {it.description && (
                  <p className="ivf-card-description">{it.description}</p>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="ivf-footer">
          <div className="ivf-dots" role="tablist" aria-label="Slider noktaları">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                className={`ivf-dot ${i === activeIndex ? 'active' : ''}`}
                onClick={() => goToPage(i)}
                aria-label={`${i + 1}. sayfaya git`}
                aria-pressed={i === activeIndex}
              />
            ))}
          </div>
        </div>
      </div>

      {selectedItem && selectedItem.content && (
        <BlogModal
          isOpen={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          title={selectedItem.title}
          date={selectedItem.date || ''}
          image={selectedItem.image}
          content={selectedItem.content}
          videoUrl={selectedItem.videoUrl}
        />
      )}
    </div>
  );
};

export default IVFSlider;
